import React, { useState } from 'react';
import { toast } from 'sonner';
import { Wand2, CheckCircle2, AlertTriangle, MinusCircle, Hourglass, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api, { formatError } from '@/lib/api';

// What the ad optimizer did (services/optimizer.py), newest first, plus a fresher version waiting for the OK.
const OUTCOMES = {
  done: { label: 'Done', icon: CheckCircle2, color: 'bg-money-soft text-money border-money/40' },
  proposed: { label: 'Needs your OK', icon: Sparkles, color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  declined: { label: 'Declined', icon: MinusCircle, color: 'bg-zinc-500/10 text-muted-foreground border-zinc-500/30' },
  failed: { label: "Couldn't apply", icon: AlertTriangle, color: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30' },
};

const hour = (h) => (h === 0 || h === 24 ? 'midnight' : h === 12 ? 'noon' : h < 12 ? `${h} am` : `${h - 12} pm`);

export function changeTitle(entry) {
  const d = entry.details || {};
  switch (entry.action) {
    case 'pause_weaker': return `Paused the ${d.which || ''} ad`.replace('  ', ' ');
    case 'best_hours': return `Runs only from ${hour(d.start_hour ?? 0)} to ${hour(d.end_hour ?? 24)}`;
    case 'widen_area': return 'Showed the ad over a wider area';
    case 'swap_version':
      return { proposed: 'Suggested a fresher version', declined: 'Kept your current version',
        failed: "Couldn't switch to the fresher version" }[entry.outcome] || 'Switched to a fresher version';
    case 'new_version':
      return { proposed: 'Made a brand-new version for you', failed: "Couldn't make a new version" }[entry.outcome]
        || 'Switched to the new version';
    default: return 'Optimised your ad';
  }
}

const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export default function OptimizerFeed({ campaign, onChange }) {
  const [busy, setBusy] = useState('');
  const log = [...(campaign.optimizer_log || [])].reverse();
  const proposal = campaign.optimizer_proposal;
  const proposed = proposal && campaign.variants?.find(v => v.id === proposal.variant_id);
  const check = campaign.optimizer_last_check;
  if (!['live', 'paused'].includes(campaign.status) && !log.length) return null;

  const answer = async (choice) => {
    setBusy(choice);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/optimizer/${choice}`);
      onChange(data);
      toast.success(choice === 'approve' ? 'Sent to Meta. Your current ad runs until it is approved.' : 'Kept your current version');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
    setBusy('');
  };

  return (
    <div className="p-4 rounded-xl bg-card border-2 border-ink" data-testid="optimizer-feed">
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-muted-foreground" /> Optimizer
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Every few hours we look at your ad&apos;s numbers and, when they call for it, make one change to get you more
        for the same budget. We never spend more than you chose.
      </p>

      {proposed && (
        <div className="p-3 rounded-lg bg-butter border-2 border-ink shadow-neo-sm mb-3" data-testid="optimizer-proposal">
          <p className="text-sm font-semibold mb-1 flex items-center gap-1.5"><Sparkles className="w-4 h-4" aria-hidden="true" />
            {proposal.new ? 'We made a brand-new version. Use it?' : 'Try a fresher version?'}</p>
          <p className="text-xs mb-2">{proposal.reason}</p>
          <div className="p-2 rounded-md bg-card border-2 border-ink/15 text-xs mb-2.5 flex gap-2.5">
            {proposed.image_url && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${proposed.image_url}`} alt="The proposed ad image"
                className="w-20 h-20 rounded-md object-cover border border-ink/15 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-semibold break-words">{proposed.headline}</p>
              <p className="text-muted-foreground mt-0.5">{proposed.primary_text}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="gradient-bg" disabled={!!busy} onClick={() => answer('approve')} data-testid="approve-swap-btn">
              {busy === 'approve' ? 'Sending...' : 'Use this version'}
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => answer('decline')} data-testid="decline-swap-btn">
              Keep current
            </Button>
          </div>
        </div>
      )}
      {campaign.swap_pending && (
        <p className="text-xs p-2.5 rounded-lg bg-butter/50 border-2 border-ink/15 mb-3 flex items-start gap-1.5">
          <Hourglass className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          Your fresher version is with Meta for review. Your current ad keeps running until it&apos;s approved.
        </p>
      )}

      {check?.note && (
        <p className="text-xs text-muted-foreground mb-3">
          <span className="font-medium text-foreground">Last check, {when(check.at)}:</span> {check.note}
        </p>
      )}

      {log.length > 0 ? (
        <ul className="space-y-2.5">
          {log.map((e, i) => {
            const o = OUTCOMES[e.outcome] || OUTCOMES.done;
            const Icon = o.icon;
            return (
              <li key={`${e.at}-${i}`} className="p-2.5 rounded-lg bg-butter/40 border-2 border-ink/15 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium">{changeTitle(e)}</span>
                  <Badge variant="outline" className={`shrink-0 text-[10px] px-2 py-0.5 ${o.color}`}>
                    <Icon className="w-3 h-3 mr-1" /> {o.label}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{when(e.at)}</p>
                {e.reason && <p className="text-xs mt-1">{e.reason}</p>}
              </li>
            );
          })}
        </ul>
      ) : !check?.note && (
        <p className="text-xs text-muted-foreground text-center py-2">No changes yet. We start once your ad has run for 2 days.</p>
      )}
    </div>
  );
}
